import * as msal from '@azure/msal-browser';
import { env } from 'src/runtime-environment';
import { store } from 'src/store';

export const Scopes = {
  AZURE_STORAGE: env.AZURE_AD_STORAGEAPI_IDENTIFIER,
  GRAPH_API: env.AZURE_AD_API_IDENTIFIER,
};

const config: msal.Configuration = {
  auth: {
    authority: `https://login.microsoftonline.com/${env.AZURE_AD_TENANT_ID}`,
    clientId: env.AZURE_AD_CLIENT_ID,
    // The configured reply url
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'localStorage',
  },
};

let provider: msal.PublicClientApplication | null = null;
let redirectHandled: Promise<void> | null = null;

// The account from the redirect response wins. Otherwise keep the account
// already stored as active. A single cached account is unambiguous. Several
// accounts stay unresolved until the next sign-in response picks one.
const settleActiveAccount = (result: msal.AuthenticationResult | null) => {
  if (result?.account) {
    provider!.setActiveAccount(result.account);
    return;
  }
  if (provider!.getActiveAccount()) return;
  const cached = provider!.getAllAccounts();
  if (cached.length === 1) {
    provider!.setActiveAccount(cached[0]);
  }
};

export const getProvider = (): msal.PublicClientApplication => {
  if (provider) return provider;

  provider = new msal.PublicClientApplication(config);

  provider.addEventCallback((message) => {
    if (
      message.eventType === msal.EventType.LOGIN_SUCCESS ||
      message.eventType === msal.EventType.ACQUIRE_TOKEN_SUCCESS
    ) {
      const result = message.payload as msal.AuthenticationResult;
      if (result?.account) provider!.setActiveAccount(result.account);
    }

    if (message.eventType === msal.EventType.LOGOUT_SUCCESS) {
      store.dispatch({ type: 'AAD_LOGOUT_SUCCESS' });
    }
  });

  // Consume the redirect hash before anything asks for a token. MSAL returns
  // this same promise if something else calls handleRedirectPromise later.
  redirectHandled = provider.handleRedirectPromise().then(settleActiveAccount);

  return provider;
};

export const whenRedirectHandled = (): Promise<void> => {
  getProvider();
  return redirectHandled!;
};

// SPA refresh tokens expire after 24h and can't be extended, so the first
// request of the day fails silent renewal (refresh token rejected, then the
// hidden-iframe retry is blocked by third-party cookie rules or times out).
// The only way out is a full-page redirect: a popup can't be opened from an
// Apollo link because there is no user gesture, so it's blocked and the app
// is left unable to make any request until site data is cleared.
const needsInteraction = (error: unknown) =>
  error instanceof msal.InteractionRequiredAuthError ||
  (error instanceof msal.BrowserAuthError &&
    error.errorCode ===
      msal.BrowserAuthErrorMessage.monitorIframeTimeoutError.code);

// Several queries fire together on load; only the first may start the
// redirect (MSAL throws interaction_in_progress otherwise). The rest wait on
// the same never-resolving promise until the page navigates away.
let pendingRedirect: Promise<never> | null = null;

const redirectForToken = (account?: msal.AccountInfo): Promise<never> => {
  if (!pendingRedirect) {
    pendingRedirect = getProvider()
      .acquireTokenRedirect({
        scopes: [Scopes.GRAPH_API],
        extraScopesToConsent: [Scopes.AZURE_STORAGE],
        account,
      })
      .then(() => new Promise<never>(() => {}));
  }
  return pendingRedirect;
};

// One interactive sign-in for the whole page. Callers share pendingRedirect,
// and every call waits until the redirect hash has been processed.
const acquireToken = async (scope: string) => {
  const pca = getProvider();
  await whenRedirectHandled();

  const account = pca.getActiveAccount();
  if (!account) {
    return redirectForToken();
  }

  try {
    return await pca.acquireTokenSilent({ scopes: [scope], account });
  } catch (error) {
    if (needsInteraction(error)) {
      return redirectForToken(account);
    }
    throw error;
  }
};

export const getBackendToken = () => acquireToken(Scopes.GRAPH_API);

export const getStorageToken = () => acquireToken(Scopes.AZURE_STORAGE);

// Used by the sign-in gate. Resolves once the API token is cached. If sign-in
// is required, starts the single redirect and never resolves (the page navigates).
export const ensureSignedIn = async (): Promise<void> => {
  await getBackendToken();
};

const isKiosk = window.location.host.includes('kiosk');
export const kioskMode = isKiosk;

// Can flick to `true` for development purposes, if/when desired.
// This is useful on mobile where you cannot use localhost & AD login requires
// https on non-localhost hosts.
const disableAuth = false;
export const authDisabled = isKiosk || disableAuth;
